import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import lodashUniq from 'lodash/fp/uniq';
import lodashCompact from 'lodash/fp/compact';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import { SR_API_ACTION_UTILITY_PROVIDER_UPSERT } from 'api/utilityActionTypes';

import {
  UTILITY_PROVIDER_TYPE_PLANS,
  UTILITY_ACTIVATION_UI_STATE_NOT_REQUIRED,
  UTILITY_ACTIVATION_UI_STATE_WARNING,
  UTILITY_ACTIVATION_ITEM_STATUS_SETUP_COMPLETED,
  UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED,
  UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT,
  UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_ACCOUNT_TRANSFER,
  UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_OTHER,
  UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_LIST,
  UTILITY_PROVIDER_SOURCE_PARTNER,
  UTILITY_PROVIDER_SOURCE_STAFF,
  UTILITY_PROVIDER_SOURCE_TENANT,
  UTILITY_PROVIDER_SOURCE_SYSTEM,
  UTILITY_TYPE_ID_INTERNET,
} from 'common/utility/constants';

import { getActivationItemStatusUiState } from 'common/utility/helpers';

import { hasRoleSalesRepManager } from 'common/utility/userRoles';

import { formatWithTwoDecimals } from 'common/formatters';
import dateStringMomentWithCurrentTime from 'common/util/dateStringMomentWithCurrentTime';
import firstWordCapitalized from 'common/util/firstWordCapitalized';
import camelCaseToTitleCase from 'common/util/camelCaseToTitleCase';
import makeFullName from 'common/util/makeFullName';
import { showFieldIf, createFormValidationFn } from 'client/helpers/formikHelpers';

import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_YELLOW,
  SR_BUTTON_THEME_ADOBE_ORANGE,
  SR_BUTTON_HEIGHT_SHORT,
} from 'client/components/SrButton/SrButton';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import UPProviderUpsertForm from 'client/components/UPProviderUpsertForm/UPProviderUpsertForm';
import { UPProviderSearchFormInner } from 'client/components/UPProviderSearchForm/UPProviderSearchForm';

const SOURCE_COLOR_MAPPING = {
  [UTILITY_PROVIDER_SOURCE_PARTNER]: 'purpleRoyal',
  [UTILITY_PROVIDER_SOURCE_STAFF]: 'gold',
  [UTILITY_PROVIDER_SOURCE_TENANT]: 'orange-300',
  [UTILITY_PROVIDER_SOURCE_SYSTEM]: 'gold',
};

const SOURCE_NAME_MAPPING = {
  [UTILITY_PROVIDER_SOURCE_PARTNER]: 'Partner',
  [UTILITY_PROVIDER_SOURCE_STAFF]: 'Admin',
  [UTILITY_PROVIDER_SOURCE_TENANT]: 'Tenant',
  [UTILITY_PROVIDER_SOURCE_SYSTEM]: 'System',
};

export const formFields = {
  serviceArea: {
    fields: ({
      item,
      addressId,
      utilityProvidersList,
      updateProviderCoveragePoints,
      onMapServiceAreaProvider,
      formikBag,
    }) => [
      {
        title: 'Coverage Map Providers',
        id: 'utilityProviderIdsInServiceArea',
        type: 'multiple_choice',
        properties: {
          onChange: async (newOptions, optionId) => {
            const utilityServiceAreaId =
              optionId && optionId.includes('serviceArea-')
                ? parseInt(optionId.replace('serviceArea-', ''), 10)
                : null;

            const providerId = optionId && !isNaN(optionId) ? parseInt(optionId, 10) : null;

            if (utilityServiceAreaId) {
              await onMapServiceAreaProvider({
                addressId,
                utilityServiceAreaId,
                utilityTypeId: item.type.id,
                companyName: item.utilityProvidersInServiceAreas.find(({ id }) => {
                  return id === utilityServiceAreaId;
                }).name,
              });
            } else {
              const existingUtilityProviderIds = item.availableProviders.map(({ id }) => id);
              if (existingUtilityProviderIds.includes(providerId)) {
                // Remove utility provider
                await updateProviderCoveragePoints({
                  addressId: addressId,
                  utilityTypeId: item.type.id,
                  utilityProviderIds: existingUtilityProviderIds.filter((id) => id !== providerId),
                });
              } else {
                // Add utility provider
                await updateProviderCoveragePoints({
                  addressId: addressId,
                  utilityTypeId: item.type.id,
                  utilityProviderIds: lodashUniq([...existingUtilityProviderIds, providerId]),
                });
              }
              formikBag.resetForm();
            }
          },
          choices: item.utilityProvidersInServiceAreas.map(({ id, utilityProviderId, name }) => {
            const utilityProvider = utilityProvidersList.find(({ id }) => id === utilityProviderId);
            return {
              label: name,
              sublabel: utilityProvider?.title || 'Connect',
              sublabelClassName: 'blueGreycliff',
              value: utilityProviderId ? String(utilityProviderId) : `serviceArea-${id}`,
            };
          }),
          multipleSelection: true,
          allowEmptyArray: true,
          selectedItemsFirst: true,
          checkboxListClassName: 'checkboxListProvider brown',
          checkboxClassName: 'checkboxProvider',
        },
      },
    ],
    initialState: ({ item }) => ({
      utilityProviderIdsInServiceArea: item.availableProviders.map(({ id }) => String(id)),
    }),
  },
  admin: {
    fields: ({
      utilityProvidersList,
      collapseProvidersTypeId,
      setCollapseProvidersByTypeId,
      requiredTypes,
      utilityType,
      onConfirmMapping,
    }) => {
      const requiredUtilityType = requiredTypes.find(
        ({ utilityTypeId }) => utilityTypeId === utilityType.id,
      );

      return [
        {
          title: 'Select the available providers',
          titleToggle: {
            text: collapseProvidersTypeId ? 'Show all providers' : 'Hide unselected providers',
            onClick: () => setCollapseProvidersByTypeId(!collapseProvidersTypeId),
          },
          id: 'coverageUtilityProviderIds',
          type: 'multiple_choice',
          properties: {
            choices: utilityProvidersList.map(({ title, id, source, sublabel }) => ({
              label: title,
              sublabel,
              sublabelClassName: SOURCE_COLOR_MAPPING[source] || 'blueGreycliff',
              value: id,
            })),
            multipleSelection: true,
            allowEmptyArray: true,
            selectedItemsFirst: true,
            selectedItemsOnly: collapseProvidersTypeId,
            checkboxListClassName: 'checkboxListProvider',
            checkboxClassName: 'checkboxProvider',
            widget: requiredUtilityType.needsMappingConfirmation ? (
              <SrBox padding="16px 0">
                <SrButton
                  theme={SR_BUTTON_THEME_ADOBE_ORANGE}
                  heightType={SR_BUTTON_HEIGHT_SHORT}
                  minWidth={null}
                  onClick={() => {
                    onConfirmMapping({
                      addressId: requiredUtilityType.addressId,
                      utilityTypeId: utilityType.id,
                    });
                  }}
                >
                  {utilityType.id === UTILITY_TYPE_ID_INTERNET
                    ? 'I confirm only Satellite or Copper is available'
                    : 'I confirm the mapping is correct'}
                </SrButton>
              </SrBox>
            ) : null,
          },
        },
      ];
    },
    initialState: ({ item }) => ({
      coverageUtilityProviderIds: item.availableProviders.map(({ id }) => id),
    }),
    onSubmit: ({ addressId, item, updateProviderCoveragePoints }) => (formValues) =>
      updateProviderCoveragePoints({
        addressId,
        utilityTypeId: item.type.id,
        utilityProviderIds: formValues.coverageUtilityProviderIds,
      }),
  },
  tenant: {
    fields: ({
      item,
      formValues,
      hasAccountIdByUtilityTypeId,
      setHasAccountIdByUtilityTypeId,
      utilityDealers,
      utilitySalesReps,
      hideUtilitySalesRepId = false,
    }) => {
      // Ensure that the provider the renter used, is available in the dropdown UI.
      const tenantSelectionProviderList =
        !item.provider || item.availableProviders.some(({ id }) => id === item.provider.id)
          ? item.availableProviders
          : [...item.availableProviders, item.provider];

      const selectedProvider = formValues.utilityProviderId
        ? tenantSelectionProviderList.find(
            ({ id }) => id === parseInt(formValues.utilityProviderId, 10),
          )
        : null;

      const availableDealersList = utilityDealers.filter(({ id, utilityProviderPlanIds }) => {
        return (
          (formValues.utilityDealerId && parseInt(formValues.utilityDealerId, 10) === id) ||
          (formValues.utilityProviderPlanId &&
            utilityProviderPlanIds.includes(parseInt(formValues.utilityProviderPlanId, 10)))
        );
      });

      const hasAccountId = hasAccountIdByUtilityTypeId[item.type.id];
      const shouldShowProofOfPurchase = item.requiresProofOfPurchase || !hasAccountId;

      return [
        {
          title: 'Tenant: What provider did they choose?',
          id: 'utilityProviderId',
          type: 'select',
          properties: {
            placeholder: '--',
            enableResetOption: true,
            choices: tenantSelectionProviderList.map(({ title, id }) => ({
              label: title,
              value: id,
            })),
          },
        },
        showFieldIf(selectedProvider?.type === UTILITY_PROVIDER_TYPE_PLANS, {
          title: 'Tenant: What plan did they choose?',
          id: 'utilityProviderPlanId',
          type: 'select',
          properties: {
            placeholder: '--',
            enableResetOption: true,
            choices: (selectedProvider?.plansAvailable || []).map(
              ({ title, id, price, utilityProviderPlanVariantId }) => ({
                label: [
                  `${title} - $${price}`,
                  utilityProviderPlanVariantId ? '- (Variant)' : '',
                ].join(' '),
                value: id,
              }),
            ),
          },
        }),
        showFieldIf(
          selectedProvider?.type === UTILITY_PROVIDER_TYPE_PLANS && !hideUtilitySalesRepId,
          {
            title: 'Sales Rep?',
            id: 'utilitySalesRepId',
            type: 'select',
            properties: {
              placeholder: '--',
              enableResetOption: true,
              choices: (utilitySalesReps || []).map(({ id, firstName, lastName }) => ({
                label: makeFullName(firstName, lastName),
                value: id,
              })),
            },
          },
        ),
        showFieldIf(formValues.utilityProviderPlanId, {
          title: 'What dealer portal was used?',
          id: 'utilityDealerId',
          type: 'select',
          properties: {
            placeholder:
              availableDealersList.length === 0
                ? 'No dealer portals have been set up for this plan.'
                : '--',
            enableResetOption: true,
            choices: (availableDealersList || []).map(({ id, name }) => ({
              label: name,
              value: id,
            })),
          },
        }),
        showFieldIf(selectedProvider || formValues.accountNumber, {
          title: 'Tenant: Account ID',
          id: 'accountNumber',
          type: 'short_text',
          titleClassName: !hasAccountId ? 'inactive' : null,
          titleToggle:
            item.type.id === UTILITY_TYPE_ID_INTERNET
              ? {}
              : {
                  text: hasAccountId ? 'No ID available?' : 'Have the ID?',
                  onClick: () => {
                    setHasAccountIdByUtilityTypeId({
                      ...hasAccountIdByUtilityTypeId,
                      [item.type.id]: !hasAccountId,
                    });
                  },
                },
          properties: {
            placeholder: 'Write here...',
            disabled: !hasAccountId,
            style: !hasAccountId ? { color: '#D4D6D8', opacity: 0.5 } : {},
          },
        }),
        showFieldIf(selectedProvider || formValues.accountNumber, {
          title: 'Tenant: Activation Date',
          id: 'activationDate',
          type: 'date_picker',
          properties: {
            placeholder: 'What date did you set for the activation?',
            dateWithoutTime: true,
          },
        }),
        showFieldIf(selectedProvider && shouldShowProofOfPurchase, {
          id: 'proofOfPurchaseUrl',
          title: 'Upload proof of purchase',
          type: 'file_single',
          properties: {
            fileAccept: '*',
            filePath:
              `utility-activations/${item.utilityActivationId}` +
              `/item-${item.id}/proof-of-purchase-{uuid}`,
          },
        }),
        showFieldIf(formValues.accountNumber && item.type.id === UTILITY_TYPE_ID_INTERNET, {
          id: 'dealerPortalConfirmationFileUrls',
          title: 'Dealer Portal: Confirmation',
          type: 'file_multi',
          properties: {
            fileAccept: '*',
            filePath:
              `utility-activations/${item.utilityActivationId}` +
              `/item-${item.id}/dealer-portal-confirmation-{uuid}`,
          },
          validations: ['nonEmptyArray'],
        }),
        ...(selectedProvider?.questions || [])
          .filter((question) => question.title)
          .map((question) => ({
            id: `utilityProviderAnswers.${question.fieldId}`,
            title: `Security Question: ${question.title}`,
            type: question.type,
            properties: {
              choices: question.choices,
            },
          })),
        {
          title: 'Archive reason?',
          id: 'dropoutReason',
          type: 'select',
          properties: {
            placeholder: '--',
            enableResetOption: true,
            choices: UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_LIST.map((value) => ({
              label: camelCaseToTitleCase(value),
              value,
            })),
          },
        },
        showFieldIf(formValues.dropoutReason, {
          title:
            formValues.dropoutReason === UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_OTHER
              ? 'Other reason'
              : `"${camelCaseToTitleCase(formValues.dropoutReason)}" explanation`,
          id: 'dropoutReasonExplanation',
          type: 'long_text',
          properties: {
            placeholder: 'Write here...',
          },
          validations:
            formValues.dropoutReason === UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_OTHER
              ? ['required']
              : [],
        }),
      ];
    },
    initialState: ({ item, utilitySalesRepId }) => ({
      utilityProviderId: String(item.utilityProviderId || ''),
      utilityProviderPlanId: String(item.utilityProviderPlanId || ''),
      utilityDealerId: String(item.utilityDealerId || ''),
      accountNumber: item.accountNumber || '',
      utilitySalesRepId: String(utilitySalesRepId || ''),
      activationDate: dateStringMomentWithCurrentTime(item.activationDate) || '',
      utilityProviderAnswers: item.utilityProviderAnswers || {},
      proofOfPurchaseUrl: item.proofOfPurchaseUrl || '',
      dropoutReason: item.dropoutReason || '',
      dropoutReasonExplanation: item.dropoutReasonExplanation || '',
      dealerPortalConfirmationFileUrls: item.dealerPortalConfirmationFileUrls || [],
    }),
    onSubmit: ({
      item,
      utilitySalesRepId,
      updateActivation,
      updateActivationItem,
      hasAccountId,
    }) => async (formValues) => {
      if (!hasAccountId) {
        formValues.accountNumber = null;
      } else {
        formValues.proofOfPurchaseUrl = null;
      }

      if (!formValues.utilityProviderPlanId) {
        formValues.utilityDealerId = null;
      }

      const newUtilitySalesRepId = parseInt(formValues.utilitySalesRepId, 10) || null;

      if (newUtilitySalesRepId !== utilitySalesRepId) {
        await updateActivation({
          id: item.utilityActivationId,
          updates: {
            utilitySalesRepId: newUtilitySalesRepId,
          },
        });
      }

      const utilityProviderId = parseInt(formValues.utilityProviderId, 10) || null;
      const utilityProviderPlanId = parseInt(formValues.utilityProviderPlanId, 10) || null;

      const selectedProvider = item.availableProviders.find(({ id }) => id === utilityProviderId);
      const selectedProvidePlan = selectedProvider?.plansAvailable.find(
        ({ id }) => id === utilityProviderPlanId,
      );

      await updateActivationItem({
        id: item.id,
        updates: {
          utilityDealerId: parseInt(formValues.utilityDealerId, 10) || null,
          utilityProviderId: utilityProviderId,
          utilityProviderPlanId: utilityProviderPlanId,
          utilityProviderPlanVariantId: selectedProvidePlan?.utilityProviderPlanVariantId || null,
          accountNumber: formValues.accountNumber || null,
          activationDate: formValues.activationDate || null,
          utilityProviderAnswers: formValues.utilityProviderAnswers || {},
          proofOfPurchaseUrl: formValues.proofOfPurchaseUrl || null,
          dropoutReason: formValues.dropoutReason || null,
          dropoutReasonExplanation: formValues.dropoutReason
            ? formValues.dropoutReasonExplanation || null
            : null,
          dealerPortalConfirmationFileUrls: formValues.dealerPortalConfirmationFileUrls || null,
        },
      });
    },
  },
};

const getNearbyProvidersProps = ({ utilityProvidersInRange, mappedProviders = [] }) => {
  return utilityProvidersInRange
    .filter((provider) => !mappedProviders.some(({ id }) => id === provider.id))
    .map((provider) => ({
      id: provider.id,
      title: provider.title,
      source: SOURCE_NAME_MAPPING[provider.source] || firstWordCapitalized(provider.source),
      sourceColor: SOURCE_COLOR_MAPPING[provider.source] || 'blueGreycliff',
      distanceInMiles:
        provider.distanceInMiles < 0.01
          ? '< 0.01'
          : formatWithTwoDecimals(provider.distanceInMiles || ''),
      url: provider.url,
    }));
};

const getSections = ({
  currentUser,
  addressId,
  items,
  requiredTypes,
  utilitySalesRepId,
  utilityTypes,
  utilityDealers,
  utilitySalesReps,
  utilityProvidersByTypeId,
  utilityProvidersInRangeByTypeId,
  utilityProvidersByAddressByTypeId,
  updateProviderCoveragePoints,
  collapseProviders,
  setCollapseProviders,
  updateActivation,
  updateActivationItem,
  hasAccountIdByUtilityTypeId,
  setHasAccountIdByUtilityTypeId,
  onMapServiceAreaProvider,
  onConfirmMapping,
}) => {
  return utilityTypes.map((type) => {
    const item = items.find(({ utilityTypeId }) => utilityTypeId === type.id);
    const utilityProvidersInRange = utilityProvidersInRangeByTypeId[type.id] || [];
    const utilityProvidersByAddress = utilityProvidersByAddressByTypeId[type.id] || [];
    const utilityProvidersList = (utilityProvidersByTypeId[type.id] || []).map((provider) => {
      const availableProviderMappedBy = item?.availableProviders.find(
        ({ id }) => id === provider.id,
      )?.mappedBy;

      return {
        ...provider,
        sublabel: lodashCompact([
          SOURCE_NAME_MAPPING[provider.source] || firstWordCapitalized(provider.source),
          availableProviderMappedBy ? ` (Mapped by ${availableProviderMappedBy})` : null,
        ]),
      };
    });

    const itemFields = !item
      ? {}
      : {
          item,
          utilityProvidersInRange: getNearbyProvidersProps({
            utilityProvidersInRange,
            mappedProviders: item.availableProviders || [],
          }),
          getServiceAreaFormFields: (formikBag) =>
            formFields.serviceArea.fields({
              item,
              addressId,
              utilityProvidersList,
              updateProviderCoveragePoints,
              onMapServiceAreaProvider,
              formikBag,
            }),
          serviceAreaFormInitialState: formFields.serviceArea.initialState({ item }),
          getAdminFormFields: () =>
            formFields.admin.fields({
              requiredTypes,
              utilityType: type,
              utilityProvidersList,
              collapseProvidersTypeId: collapseProviders[type.id],
              setCollapseProvidersByTypeId: (value) => {
                setCollapseProviders({
                  ...collapseProviders,
                  [type.id]: value,
                });
              },
              onConfirmMapping,
            }),
          adminFormInitialState: formFields.admin.initialState({ item }),
          onAdminFormSubmit: formFields.admin.onSubmit({
            addressId,
            item,
            updateProviderCoveragePoints,
          }),

          tenantFormInitialState: formFields.tenant.initialState({ item, utilitySalesRepId }),
          getTenantFormFields: (formValues) =>
            formFields.tenant.fields({
              item,
              formValues,
              utilityProvidersList,
              utilityDealers,
              utilitySalesReps,
              hasAccountIdByUtilityTypeId,
              setHasAccountIdByUtilityTypeId,
              hideUtilitySalesRepId: !hasRoleSalesRepManager(currentUser.roles),
            }),
          onTenantFormSubmit: formFields.tenant.onSubmit({
            item,
            utilitySalesRepId,
            updateActivation,
            updateActivationItem,
            hasAccountId: hasAccountIdByUtilityTypeId[type.id],
          }),
        };

    return {
      type,
      ui: getActivationItemStatusUiState({
        status: item?.status,
        completedStates: [
          UTILITY_ACTIVATION_ITEM_STATUS_SETUP_COMPLETED,
          UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED,
          UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT,
        ],
        label: type.label,
        utilityProviders: utilityProvidersByAddress,
      }),
      ...itemFields,
    };
  });
};

const SrUtilityProfitAdminProcessActivationForm = ({
  currentUser,
  utilityActivation,
  utilityProvidersByTypeId,
  utilityProvidersInRangeByTypeId = {},
  utilityProvidersByAddressByTypeId = {},
  utilityTypes,
  utilityDealers,
  utilitySalesReps,
  updateProviderCoveragePoints,
  updateActivation,
  updateActivationItem,
  toggleRequiredType,
  onMapServiceAreaProvider,
  onConfirmMapping,
}) => {
  const initialCollapseProviders = utilityTypes.reduce((acc, type) => {
    acc[type.id] = true;
    return acc;
  }, {});

  const [collapseProviders, setCollapseProviders] = useState(initialCollapseProviders);
  const [upsertProviderModalArgs, setUpsertProviderModalArgs] = useState(null);
  const dispatch = useDispatch();

  const [hasAccountIdByUtilityTypeId, setHasAccountIdByUtilityTypeId] = useState(
    utilityActivation.items.reduce((acc, item) => {
      if (item.type.id === UTILITY_TYPE_ID_INTERNET) {
        acc[item.utilityTypeId] = true;
        return acc;
      }

      acc[item.utilityTypeId] = !!item.accountNumber || !item.proofOfPurchaseUrl;
      return acc;
    }, {}),
  );

  const sections = getSections({
    currentUser,
    addressId: utilityActivation.addressId,
    items: utilityActivation.items,
    requiredTypes: utilityActivation.requiredTypes,
    utilitySalesRepId: utilityActivation.utilitySalesRepId,
    utilityProvidersByTypeId,
    utilityProvidersInRangeByTypeId,
    utilityProvidersByAddressByTypeId,
    utilityTypes,
    utilityDealers,
    utilitySalesReps,
    updateProviderCoveragePoints,
    updateActivation,
    updateActivationItem,
    hasAccountIdByUtilityTypeId,
    setHasAccountIdByUtilityTypeId,
    collapseProviders,
    setCollapseProviders,
    onMapServiceAreaProvider,
    onConfirmMapping,
  });

  const _handleProviderUpsert = ({ utilityType, currentUtilityProviderIds }) => {
    if (!utilityType || utilityType.id === UTILITY_TYPE_ID_INTERNET) {
      return;
    }
    setUpsertProviderModalArgs({
      utilityType,
      initialValues: {
        addressId: utilityActivation.addressId,
        utilityTypeId: utilityType.id,
      },
      addressDisplay: utilityActivation.address.display,
      onCancel: () => setUpsertProviderModalArgs(null),
      onSubmit: (utilityProviderUpsertForm) => {
        dispatch(requestUtilityApi, {
          action: SR_API_ACTION_UTILITY_PROVIDER_UPSERT,
          payload: utilityProviderUpsertForm,
          onSuccess: ({ utilityProvider }) => {
            setUpsertProviderModalArgs(null);
            updateProviderCoveragePoints({
              addressId: utilityActivation.addressId,
              utilityTypeId: utilityType.id,
              utilityProviderIds: lodashUniq([
                ...(currentUtilityProviderIds || []),
                utilityProvider.id,
              ]),
            });
          },
        });
      },
      onRemoveCoverage: () => {
        setUpsertProviderModalArgs(null);
      },
    });
  };

  return (
    <div>
      {sections.map((section) => (
        <SrBox
          padding="23px 25px"
          borderTop="1px solid #e9ebec"
          key={section.type.id}
          id={`section-${section.type.label}`}
        >
          <SrFlex justify="between">
            <SrFlex columnGap="4px" align="center">
              <SrBox
                width="14px"
                height="14px"
                backgroundImage={`url("${section.ui.iconUrl}")`}
                backgroundSize="cover"
                marginRight="5px"
              />
              <SrText
                font="greycliff"
                size="18"
                weight="700"
                decoration={
                  section.ui.uiState === UTILITY_ACTIVATION_UI_STATE_NOT_REQUIRED
                    ? 'line-through'
                    : undefined
                }
                color={
                  section.ui.uiState === UTILITY_ACTIVATION_UI_STATE_NOT_REQUIRED
                    ? section.ui.color
                    : undefined
                }
              >
                {section.ui.uiState === UTILITY_ACTIVATION_UI_STATE_WARNING
                  ? section.type.label
                  : section.ui.label}
              </SrText>
              {section.ui.uiState !== UTILITY_ACTIVATION_UI_STATE_NOT_REQUIRED && (
                <SrBox
                  width="19px"
                  height="19px"
                  backgroundImage={`url("${section.type.iconUrl}")`}
                  backgroundSize="cover"
                />
              )}
            </SrFlex>
            <SrText
              font="greycliff"
              size="16"
              weight="600"
              color="blue300"
              cursor="pointer"
              onClick={() => toggleRequiredType(section.type.id)}
            >
              {`Mark as ${section.item ? 'Not ' : ''}Required`}
            </SrText>
          </SrFlex>
          {section.utilityProvidersInRange && section.utilityProvidersInRange.length > 0 && (
            <Formik
              initialValues={section.adminFormInitialState}
              enableReinitialize
              onSubmit={section.onAdminFormSubmit}
              render={(formikBag) => {
                return (
                  <SrBox marginTop="20px" padding="0 22px">
                    <SrText
                      font="greycliff"
                      fontSize="21px"
                      weight="800"
                      lineHeight="25px"
                      display="block"
                    >
                      Nearby Providers
                    </SrText>
                    <SrBox padding="13px 0" display="grid" gridTemplateColumns="1fr 1fr 1fr">
                      {section.utilityProvidersInRange.map((provider) => (
                        <SrBox key={`nearby_provider_${provider.id}`} margin="7px 0">
                          <SrText
                            font="greycliff"
                            fontSize="10px"
                            lineHeight="17px"
                            weight="700"
                            display="block"
                            color={provider.sourceColor}
                          >
                            {provider.source}
                          </SrText>
                          <SrText
                            font="greycliff"
                            fontSize="14px"
                            lineHeight="17px"
                            weight="700"
                            paddingRight="4px"
                          >
                            {provider.title}
                          </SrText>
                          <SrText
                            font="greycliff"
                            fontSize="14px"
                            lineHeight="17px"
                            fontStyle="italic"
                          >
                            {provider.distanceInMiles} miles
                          </SrText>
                          <SrBox display="flex" alignItems="center">
                            <SrBox
                              marginRight="10px"
                              onClick={() => {
                                const selectedProviderIds = lodashUniq([
                                  ...(formikBag.values.coverageUtilityProviderIds || []),
                                  provider.id,
                                ]);
                                formikBag.setFieldValue(
                                  'coverageUtilityProviderIds',
                                  selectedProviderIds,
                                );
                                formikBag.validateForm().then(() => {
                                  formikBag.handleSubmit();
                                });
                              }}
                            >
                              <SrText
                                font="greycliff"
                                fontSize="14px"
                                lineHeight="17px"
                                display="block"
                                color="blue300"
                                cursor="pointer"
                              >
                                Map provider
                              </SrText>
                            </SrBox>
                            {provider.url && (
                              <SrLink target="_blank" to={provider.url}>
                                <SrText
                                  font="greycliff"
                                  fontSize="14px"
                                  lineHeight="17px"
                                  display="block"
                                  color="blue300"
                                >
                                  Go to site
                                </SrText>
                              </SrLink>
                            )}
                          </SrBox>
                        </SrBox>
                      ))}
                    </SrBox>
                  </SrBox>
                );
              }}
            />
          )}
          {section.item?.utilityProvidersInServiceAreas?.length > 0 && (
            <SrBox marginTop="20px" padding="0 22px">
              <Formik
                initialValues={section.serviceAreaFormInitialState}
                enableReinitialize
                render={(formikBag) => {
                  return (
                    <Form>
                      {section.getServiceAreaFormFields(formikBag).map((formField) => (
                        <SrFormikField
                          key={formField.id}
                          margin="13px 0 25px"
                          formField={formField}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      ))}
                    </Form>
                  );
                }}
              />
            </SrBox>
          )}
          {section.item && (
            <SrBox padding="0 22px">
              <Formik
                initialValues={section.adminFormInitialState}
                enableReinitialize
                onSubmit={section.onAdminFormSubmit}
                render={(formikBag) => {
                  return (
                    <Form>
                      <UPProviderSearchFormInner
                        addressDisplay={utilityActivation.address.display}
                        utilityType={section.type}
                        onAddNew={({ formikBag: searchFormikBag }) => {
                          const currentUtilityProviderIds = [
                            ...(formikBag.values.coverageUtilityProviderIds || []),
                          ];
                          searchFormikBag.resetForm();
                          return _handleProviderUpsert({
                            utilityType: section.type,
                            currentUtilityProviderIds,
                          });
                        }}
                        onProviderSelected={(provider) => {
                          const selectedProviderIds = lodashUniq([
                            ...(formikBag.values.coverageUtilityProviderIds || []),
                            provider.id,
                          ]);
                          formikBag.setFieldValue(
                            'coverageUtilityProviderIds',
                            selectedProviderIds,
                          );
                          formikBag.validateForm().then(() => {
                            formikBag.handleSubmit();
                          });
                        }}
                      />
                      {section.getAdminFormFields().map((formField) => (
                        <SrFormikField
                          key={formField.id}
                          margin="13px 0 25px"
                          formField={formField}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      ))}
                      <SrButton
                        theme={SR_BUTTON_THEME_ADOBE_GREEN}
                        disabled={!formikBag.isValid || !formikBag.dirty}
                        width="326px"
                        margin="0 0 14px"
                        hasBoxShadow
                        type="submit"
                      >
                        Save
                      </SrButton>
                    </Form>
                  );
                }}
              />

              {section.item.dropoutReason ===
                UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_ACCOUNT_TRANSFER && (
                <SrText font="greycliff" size="21" weight="800">
                  {`Tenant purchased ${section.item.type.label.toLowerCase()} outside of this tool.`}
                </SrText>
              )}

              <Formik
                initialValues={section.tenantFormInitialState}
                enableReinitialize
                onSubmit={section.onTenantFormSubmit}
                validate={(values) => {
                  const formFields = section.getTenantFormFields(values);
                  return createFormValidationFn(formFields)(values);
                }}
                render={(formikBag) => {
                  const formFields = section.getTenantFormFields(formikBag.values);
                  if (formFields.every((field) => field.type === 'hidden')) {
                    return null;
                  }
                  return (
                    <Form>
                      {formFields.map((formField) => (
                        <SrFormikField
                          key={formField.id}
                          margin="13px 0 25px"
                          formField={formField}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      ))}
                      <SrButton
                        theme={SR_BUTTON_THEME_ADOBE_YELLOW}
                        disabled={!formikBag.isValid || !formikBag.dirty}
                        width="326px"
                        margin="0 0 14px"
                        hasBoxShadow
                        type="submit"
                      >
                        Save
                      </SrButton>
                    </Form>
                  );
                }}
              />
            </SrBox>
          )}
        </SrBox>
      ))}
      {upsertProviderModalArgs && <UPProviderUpsertForm {...upsertProviderModalArgs} />}
    </div>
  );
};

SrUtilityProfitAdminProcessActivationForm.propTypes = {
  utilityActivation: PropTypes.object.isRequired,
  utilityProvidersByTypeId: PropTypes.object.isRequired,
  utilityProvidersInRangeByTypeId: PropTypes.object,
  utilityTypes: PropTypes.array.isRequired,
  utilityDealers: PropTypes.array.isRequired,
  utilitySalesReps: PropTypes.array.isRequired,
  updateProviderCoveragePoints: PropTypes.func.isRequired,
  updateActivation: PropTypes.func.isRequired,
  updateActivationItem: PropTypes.func.isRequired,
  toggleRequiredType: PropTypes.func.isRequired,
  onMapServiceAreaProvider: PropTypes.func.isRequired,
};

SrUtilityProfitAdminProcessActivationForm.displayName = 'SrUtilityProfitAdminProcessActivationForm';

export default SrUtilityProfitAdminProcessActivationForm;
